import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

const AccountAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { handleAdd, loading, resetForm } = useAdd("shop/accounts", token);

  const selectedBranch = auth?.user?.selected_branch
    ? String(auth.user.selected_branch)
    : auth?.user?.user?.selected_branch
      ? String(auth.user.user.selected_branch)
      : "";

  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );

  const shops = useMemo(
    () => (Array.isArray(shopsData) ? shopsData : shopsData?.results || []),
    [shopsData]
  );
  const hasSingleShop = shops.length === 1;

  const [selectedShop, setSelectedShop] = useState("");
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currencies", "shop-account-add", selectedShop],
    selectedShop ? `shop/currencies/?page_size=all&shop=${selectedShop}` : "",
    token
  );

  const currencies = Array.isArray(currenciesData)
    ? currenciesData
    : currenciesData?.results || [];

  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("normal");
  const [currencyIds, setCurrencyIds] = useState([]);
  const [openingBalances, setOpeningBalances] = useState({});
  const [note, setNote] = useState("");

  useEffect(() => {
    if (shopLoading || shops.length === 0) return;
    const branchShop = selectedBranch
      ? shops.find((shop) => String(shop.id) === selectedBranch)
      : null;
    if (branchShop) {
      setSelectedShop(String(branchShop.id));
    } else if (hasSingleShop) {
      setSelectedShop(String(shops[0].id));
    } else if (!selectedShop) {
      setSelectedShop(String(shops[0].id));
    }
  }, [hasSingleShop, selectedBranch, selectedShop, shopLoading, shops]);

  useEffect(() => {
    setCurrencyIds([]);
    setOpeningBalances({});
  }, [selectedShop]);

  const handleToggleCurrency = (id) => {
    setCurrencyIds((prev) => {
      const next = prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id];
      // Remove opening balance for unchecked currency
      if (!next.includes(id)) {
        setOpeningBalances((b) => { const n = { ...b }; delete n[id]; return n; });
      }
      return next;
    });
  };

  const handleSubmit = () => {
    // Build opening_balances dict: { currencyId: amount }
    const openingBalancesPayload = {};
    currencyIds.forEach((id) => {
      openingBalancesPayload[id] = openingBalances[id] || "0";
    });

    handleAdd({
      shop: selectedShop,
      name: name.trim(),
      account_type: accountType,
      currency: currencyIds[0] || "",
      currencies: currencyIds.map(Number),
      opening_balance: openingBalances[currencyIds[0]] || "0",
      opening_balances: openingBalancesPayload,
      note: note.trim() || null,
    });
  };

  useEffect(() => {
    if (resetForm) {
      if (selectedBranch) {
        setSelectedShop(selectedBranch);
      } else if (hasSingleShop) {
        setSelectedShop(String(shops[0].id));
      } else if (shops.length) {
        setSelectedShop(String(shops[0].id));
      } else {
        setSelectedShop("");
      }
      setName("");
      setAccountType("normal");
      setCurrencyIds([]);
      setOpeningBalances({});
      setNote("");
      onClose();
    }
  }, [resetForm, onClose, hasSingleShop, selectedBranch, shops]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("account.addAccount")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("account.name")}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          {/* Currency — inline checkboxes on one line */}
          <FormControl
            component="fieldset"
            margin="dense"
            fullWidth
            required
            disabled={!selectedShop || currencyLoading}
            sx={{ mt: 1 }}
          >
            <FormLabel component="legend" sx={{ fontSize: 12, mb: 0.5 }}>
              {t("account.currency")} *
            </FormLabel>
            {!selectedShop ? (
              <Box sx={{ fontSize: 13, color: "text.secondary" }}>
                {t("account.selectShopFirst")}
              </Box>
            ) : currencyLoading ? (
              <CircularProgress size={20} />
            ) : currencies.length === 0 ? (
              <Box sx={{ fontSize: 13, color: "text.secondary" }}>
                {t("account.noCurrencies")}
              </Box>
            ) : (
              <FormGroup
                row
                sx={{ flexWrap: "nowrap", gap: 0, overflowX: "auto" }}
              >
                {currencies.map((c) => (
                  <FormControlLabel
                    key={c.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={currencyIds.includes(String(c.id))}
                        onChange={() => handleToggleCurrency(String(c.id))}
                        sx={{ p: 0.4 }}
                      />
                    }
                    label={c.title}
                    sx={{
                      ml: 0,
                      mr: 1.5,
                      whiteSpace: "nowrap",
                      "& .MuiFormControlLabel-label": { fontSize: 13, ml: 0.5 },
                    }}
                  />
                ))}
              </FormGroup>
            )}
          </FormControl>

          {/* Opening balance — one input per selected currency, all on one line */}
          {currencyIds.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 1.5, mt: 0.5, overflowX: "auto" }}>
              {currencyIds.map((id) => {
                const c = currencies.find((x) => String(x.id) === String(id));
                return (
                  <TextField
                    key={id}
                    margin="dense"
                    label={`Opening Balance ${c?.title || id}`}
                    type="number"
                    inputProps={{ step: "0.01" }}
                    sx={{ minWidth: 160 }}
                    value={openingBalances[id] ?? "0"}
                    onChange={(e) =>
                      setOpeningBalances((prev) => ({ ...prev, [id]: e.target.value }))
                    }
                  />
                );
              })}
            </Box>
          )}

          <TextField
            margin="dense"
            label={t("account.note")}
            fullWidth
            multiline
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("account.type")}
            fullWidth
            required
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
          >
            <MenuItem value="normal">{t("account.normal", "Normal")}</MenuItem>
            <MenuItem value="sarap">{t("account.sarap", "Sarap")}</MenuItem>
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("account.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            shopLoading ||
            !selectedShop ||
            !name.trim() ||
            currencyIds.length === 0 ||
            !accountType
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("account.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountAdd;
